import { useEffect, useRef, useState } from 'react';

/** Czas tweenu licznika (plan E5.1, §1.33): 400 ms, ease-out. */
export const TWEEN_MS = 400;

/** Ease-out (cubic): szybki start, miękkie dojście — ta sama krzywa co `tweenCount` w makietach. */
export function easeOutCubic(k: number): number {
  return 1 - Math.pow(1 - k, 3);
}

function reducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Liczba ofert do POKAZANIA, gdy docelowa jest `target`.
 *
 * Przy każdej zmianie `target` zwracana liczba przechodzi od poprzedniej do nowej w `TWEEN_MS`
 * (`requestAnimationFrame`, bez biblioteki), zaokrąglona do całych. Pierwsza liczba pojawia
 * się od razu (nie ma od czego liczyć), a `prefers-reduced-motion: reduce` skacze wprost do
 * końca. Zmiana celu w trakcie tweenu rusza od tego, co jest na ekranie, nie od starego celu.
 *
 * `target = null` znaczy „jeszcze liczymy” (szkielet) — hook ODDAJE wtedy ostatnią znaną
 * liczbę, nie `null`, żeby po szkielecie licznik płynął od poprzedniej wartości, a nie od zera.
 * Kto rysuje szkielet, decyduje po własnym `target == null`, nie po wyniku hooka. `null`
 * wraca tylko zanim przyszła pierwsza liczba.
 *
 * Słowo po liczbie („oferta/oferty/ofert”) ma przełączać się OD RAZU na formę końcową — więc
 * `count` dla i18n bierze się z `target`, a stąd tylko wartość do `formatLiczby`.
 */
export function useAnimatedCount(target: number | null): number | null {
  const [shown, setShown] = useState<number | null>(target);
  // Ta sama wartość co `shown`, czytelna z wnętrza pętli klatek bez domknięcia na stary stan.
  const shownRef = useRef<number | null>(target);

  useEffect(() => {
    if (target == null) return;
    const from = shownRef.current;
    if (from == null || from === target || reducedMotion()) {
      shownRef.current = target;
      setShown(target);
      return;
    }
    let t0: number | null = null;
    let raf = 0;
    const step = (t: number) => {
      if (t0 == null) t0 = t;
      const k = Math.min(1, (t - t0) / TWEEN_MS);
      const v = Math.round(from + (target - from) * easeOutCubic(k));
      shownRef.current = v;
      setShown(v);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  return shown ?? target;
}
